'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { TradeStoryOverview } from './TradeStoryOverview';
import { TradeStoryOverviewListSkeleton } from '@/components/trader/TradeStoryOverviewListSkeleton';
import { StoriesModal } from '@/components/trader/StoriesModal';
import { useStorySimple } from '@/lib/hooks/useNewStoryData';

interface TradeStoryOverviewListProps {
  address: string;
}

export function TradeStoryOverviewList({
  address,
}: TradeStoryOverviewListProps) {
  const t = useTranslations();
  const { data, isLoading } = useStorySimple(address);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');

  const handleStoryPress = (symbol: string) => {
    setSelectedSymbol(symbol);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedSymbol('');
  };

  // Show skeleton while loading
  if (isLoading) {
    return <TradeStoryOverviewListSkeleton />;
  }

  // Don't render if no data or empty list
  if (!data || data.list.length === 0) {
    return null;
  }

  return (
    <VStack gap={6} p={6} mt={6} align="start" w="full" border=" 1px solid rgba(255, 255, 255, 0.15)" rounded="16px">
      {/* Section Title */}
      <Text fontSize="xl" color="text.t1/90" fontWeight="700">
        {t('trader.tradingStories')}
      </Text>

      {/* Horizontal Scrollable List */}
      <HStack overflowX="auto" scrollbar="hidden" maxW="full" gap={1}>
        {data.list.map((item) => (
          <TradeStoryOverview
            key={item.symbol}
            symbol={item.symbol}
            tradeCount={item.count}
            pnl={item.totalPnl}
            onPress={() => {
              handleStoryPress(item.symbol);
            }}
          />
        ))}
      </HStack>

      <StoriesModal
        visible={modalVisible}
        onClose={handleModalClose}
        address={address}
        symbol={selectedSymbol}
      />
    </VStack>
  );
}
